const termsContent = `# Naptune Terms and Conditions

_Last updated: April 27, 2025_

By accessing or using Naptune ("we", "our", "us", the "Platform"), you ("you", "your", "User") agree to be bound by the following Terms and Conditions. If you do not agree, please do not use our services.

---

## Third-Party Terms

By using Naptune, you agree to be bound by the following third-party terms of service:

- **YouTube Terms of Service**: [https://www.youtube.com/t/terms](https://www.youtube.com/t/terms)
- **Google Privacy Policy**: [https://policies.google.com/privacy](https://policies.google.com/privacy)

Your use of YouTube features through Naptune is subject to YouTube's terms and policies.

---

## 1. Usage of YouTube API Credentials
- The functionality of Naptune requires integration with YouTube APIs.
- You agree to provide valid YouTube API credentials for uploading and managing your content.
- All personal data, including but not limited to API credentials and tokens, is securely encrypted and stored in our database.
- We implement industry-standard security measures to protect your information, but cannot guarantee absolute security.

## 2. Ownership and Licensing of Uploaded Content
- You retain full ownership of the audio, image, and video content you upload.
- By using the Platform, you grant Naptune a non-exclusive, royalty-free, worldwide license to display, embed, promote, and share your uploaded content across our affiliated platforms and future projects.

## 3. Deconstruction and Tagging of Audio Files
- You agree that Naptune may analyze, deconstruct, and tag your audio files to improve search engine capabilities and enhance user discovery.
- This includes metadata extraction, descriptive tagging, and pattern analysis.

## 4. User Responsibilities
- You are solely responsible for ensuring you have the rights and permissions to upload and distribute the content submitted.
- You agree not to upload content that infringes upon intellectual property, privacy rights, or any other rights of third parties.
- You agree not to use the Platform for illegal, abusive, or unauthorized purposes.

## 5. Disclaimer of Warranties
- Naptune is provided "as-is" and "as-available."
- We make no warranties, express or implied, regarding the reliability, availability, or accuracy of our services.
- We do not guarantee uninterrupted, secure, or error-free operations, including successful uploads or YouTube integrations.

## 6. Limitation of Liability
- Naptune and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform.
- Our total liability shall not exceed the amount you paid (if any) for services in the previous six months.

## 7. Indemnification
- You agree to indemnify and hold harmless Naptune, its affiliates, and partners from any claims, damages, liabilities, and expenses arising out of your use of the Platform, uploaded content, or violation of these Terms.

## 8. Termination
- We reserve the right to suspend or terminate your access to Naptune at any time, without notice, for any behavior we determine is harmful, abusive, or in violation of these Terms.

## 9. Changes to Terms
- We may revise these Terms at any time.
- Continued use of the Platform after changes become effective constitutes your acceptance of the new Terms.

## 10. Third Party Services Disclaimer
- The Platform relies on third-party services (e.g., YouTube).
- We are not responsible for disruptions, restrictions, or changes made by these services.

## 11. Content Moderation Rights
- We reserve the right to remove, block, or restrict access to any content that violates these Terms or is otherwise objectionable.

## 12. Data Retention
- User data may be retained even after account deletion for backup, legal compliance, or auditing purposes.

## 13. Age Restriction
- You must be at least 13 years old (or the minimum legal age in your jurisdiction) to use Naptune.

## 14. Governing Law
- These Terms are governed by and construed under the laws of Serbia, without regard to conflict of law principles.

## 15. Open Source Software

Naptune utilizes third-party open-source software to provide its services:

### FFmpeg Multimedia Framework
- Naptune uses FFmpeg for video processing and encoding
- FFmpeg is licensed under the GNU Lesser General Public License (LGPL) version 2.1
- Naptune does not claim ownership over FFmpeg
- A copy of the LGPL license can be found at: https://www.gnu.org/licenses/old-licenses/lgpl-2.1.html
- We use FFmpeg libraries in accordance with the license terms, including dynamic linking where applicable

### Reverse Engineering Exception

Users may not reverse engineer, decompile, or disassemble the Platform, **except** as permitted by the LGPL v2.1 for the purpose of debugging modifications to the FFmpeg libraries used by Naptune.

### Source Code Availability
For a complete list of open-source components and their licenses, please visit our [Open Source Licenses](/licenses) page.

---

# Privacy Policy (Summary)

We value your privacy. Our key practices:
- Personal credentials (such as YouTube API tokens) are encrypted.
- We do not sell your personal data.
- We use cookies and analytics to improve user experience.
- You may request deletion of your personal data by contacting us at naptune.global@gmail.com.

For a full Privacy Policy, please visit [Insert Link to Privacy Policy].

---

# DMCA Notice

If you believe any content uploaded to Naptune infringes upon your copyrights, please send a detailed takedown request to naptune.global@gmail.com. We will respond in accordance with the Digital Millennium Copyright Act (DMCA).

---

_Thank you for using Naptune!_
`;

export default termsContent;